import z from "zod";
import { Constants } from "@/features/shared/constants";
import type { BestillerSelskap } from "../context/bestillingContext";

export const translationPrefixSelskapForm = "pages.bestiller.selskap.form";

const jaNeiOptions = ["ja", "nei"];
export const næringsdrivendeSchema = z.enum(jaNeiOptions, {
  error: `${translationPrefixSelskapForm}.feil.næringsdrivende.påkrevd`,
});

const kundenummerSchema = z
  .string()
  .nonempty(`${translationPrefixSelskapForm}.feil.kundenummer.påkrevd`);
const målenummerSchema = z.string().optional();

const antallBoenheterSchema = z.string().optional();

const replaceCommasWithDots = (val: unknown): string | undefined => {
  if (typeof val === "string") {
    return val.replace(",", ".");
  }
  return undefined as string | undefined;
};

const næringsarealProsent = z
  .string()
  .optional()
  .transform((val) => replaceCommasWithDots(val))
  .refine(
    (val) => {
      if (val === undefined || val === "") return true; // Optional field
      // Check for empty, NaN, not integer or more than 2 decimals
      return /^\d{1,2}(?:[.,]\d{0,2})?$/.test(val);
    },
    {
      message: `${translationPrefixSelskapForm}.feil.næringsarealProsent.maks-to-desimaler`,
    },
  )
  .refine(
    (val) => {
      const tall = replaceCommasWithDots(val);
      if (tall === undefined || tall === "") return true; // Optional field
      const valNum = Number(tall);
      return valNum >= 1 && valNum <= 100;
    },
    {
      message: `${translationPrefixSelskapForm}.feil.næringsarealProsent.minMax`,
    },
  );

export const bestillerSelskapFormSchema = z
  .object({
    kundenummer: kundenummerSchema,
    målerId: målenummerSchema,
    antallBoenheter: antallBoenheterSchema,
    næringsdrivende: næringsdrivendeSchema.optional(),
    næringsarealProsent: næringsarealProsent,
    boligType: z
      .enum(Constants.SELSKAPS_BOLIGTYPER, {
        // Overrides the default "Invalid enum value. Expected ..." message
        error: () => ({
          message: `${translationPrefixSelskapForm}.feil.boligType.påkrevd`,
        }),
      })
      .optional()
      .refine(
        (val) =>
          val !== undefined && Constants.SELSKAPS_BOLIGTYPER.includes(val),
        {
          message: `${translationPrefixSelskapForm}.feil.boligType.påkrevd`,
        },
      ),
  })
  .refine(
    (data) => {
      if (data.næringsdrivende === "ja" || data.boligType === "Andre") {
        return data.næringsarealProsent; // Validation of number is done in the schema
      }
      // Ikke næringsdrivende, ikke næringsbygg med boliger - har ingen prosent
      return true;
    },
    {
      message: `${translationPrefixSelskapForm}.feil.næringsarealProsent.paakrevd`,
      path: ["næringsarealProsent"],
    },
  )
  .refine(
    (data) => {
      const isNæringsbyggMedBoliger = data.boligType === "Andre";
      if (isNæringsbyggMedBoliger) {
        return true; // Antall boenheter skal alltid være 1 for næringsbygg med boliger, styres ikke av brukeren
      }
      const boenheter = Number(data.antallBoenheter);
      return (
        data.antallBoenheter !== "" && !Number.isNaN(boenheter) && boenheter > 0
      );
    },
    {
      message: `${translationPrefixSelskapForm}.feil.antallBoenheter.positiv`,
      path: ["antallBoenheter"],
    },
  )
  .refine(
    (data) => {
      if (data.boligType === "Andre") {
        return true; // Næringsbygg med boliger er alltid næringsdrivende, styres ikke av brukeren
      }
      const valid =
        data.næringsdrivende !== undefined &&
        jaNeiOptions.includes(data.næringsdrivende);
      return valid;
    },
    {
      message: `${translationPrefixSelskapForm}.feil.næringsdrivende.påkrevd`,
      path: ["næringsdrivende"],
    },
  );

export type bestillerSelskapFormValues = z.infer<
  typeof bestillerSelskapFormSchema
>;

export function getDefaultBestillerSelskapFormValues(
  bestillerSelskap: BestillerSelskap | null,
): bestillerSelskapFormValues {
  return {
    kundenummer: bestillerSelskap?.kundenummer ?? "",
    målerId: bestillerSelskap?.målerId ?? "",
    antallBoenheter: `${bestillerSelskap?.antallBoenheter ?? ""}`,
    næringsdrivende:
      bestillerSelskap?.næringsdrivende === true
        ? "ja"
        : bestillerSelskap?.næringsdrivende === false
          ? "nei"
          : undefined,
    næringsarealProsent:
      bestillerSelskap?.næringsarealProsent?.toString() || undefined,
    boligType: bestillerSelskap?.boligType,
  };
}
